import React, { useEffect, useState, useContext } from 'react';
import { getErrorMessage } from '../utils/errorHandler';
import axios from 'axios';
import { AuthContext } from '../App';
import Navbar from '../components/Navbar';
import PermissionDeniedCard from '../components/PermissionDeniedCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { MessageSquare } from 'lucide-react';
import usePermissions from '../hooks/usePermissions';

const messagePreviewStyle = {
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 4,
  overflow: 'hidden'
};

export default function MemberNewsPage() {
  const { user } = useContext(AuthContext);
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  const canViewBoard = hasPermission('BOARD_VIEW');
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedNews, setSelectedNews] = useState(null);
  const [editingNews, setEditingNews] = useState(null);
  const [countries, setCountries] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  
  const [formData, setFormData] = useState({
    news_type: 'announcement', // announcement or city_change
    title: '',
    message: '',
    link: '',
    country_id: null,
    province_id: null,
    city_id: null,
    arrival_date: '',
    end_date: '',
    permanent: false
  });

  useEffect(() => {
    if (permissionsLoading || !canViewBoard) return;
    fetchNews();
    fetchCountries();
  }, [canViewBoard, permissionsLoading]);

  const fetchCountries = async () => {
    try {
      const response = await axios.get('/countries');
      setCountries(response.data);
    } catch (error) {
      console.error('Failed to fetch countries', error);
    }
  };

  const fetchProvinces = async (countryId) => {
    try {
      const response = await axios.get(`/countries/${countryId}/provinces`);
      setProvinces(response.data);
    } catch (error) {
      console.error('Failed to fetch provinces', error);
    }
  };

  const fetchCities = async (provinceId) => {
    try {
      const response = await axios.get(`/provinces/${provinceId}/cities`);
      setCities(response.data);
    } catch (error) {
      console.error('Failed to fetch cities', error);
    }
  };

  const fetchNews = async () => {
    try {
      const response = await axios.get('/member-news');
      setNews(response.data);
    } catch (error) {
      console.error('Failed to fetch news', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCountryChange = (countryId) => {
    setFormData(prev => ({ ...prev, country_id: parseInt(countryId), province_id: null, city_id: null }));
    setProvinces([]);
    setCities([]);
    if (countryId) fetchProvinces(countryId);
  };

  const handleProvinceChange = (provinceId) => {
    setFormData(prev => ({ ...prev, province_id: parseInt(provinceId), city_id: null }));
    setCities([]);
    if (provinceId) fetchCities(provinceId);
  };

  const handleDelete = async (newsId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo annuncio?')) return;
    
    try {
      await axios.delete(`/member-news/${newsId}`);
      toast.success('Annuncio eliminato con successo');
      fetchNews();
    } catch (error) {
      console.error('Failed to delete news', error);
      toast.error(getErrorMessage(error, 'Errore durante l\'eliminazione'));
    }
  };

  const handleEdit = (item) => {
    setEditingNews(item);
    setFormData({
      news_type: item.news_type,
      title: item.title,
      message: item.message,
      link: item.link || '',
      country_id: item.country_id || null,
      province_id: item.province_id || null,
      city_id: item.city_id || null,
      arrival_date: item.arrival_date ? item.arrival_date.split('T')[0] : '',
      end_date: item.end_date ? item.end_date.split('T')[0] : '',
      permanent: item.permanent || false
    });
    if (item.country_id) {
      fetchProvinces(item.country_id);
    }
    if (item.province_id) {
      fetchCities(item.province_id);
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.news_type === 'city_change') {
      const missing = [];
      if (!formData.country_id) missing.push('nazione');
      if (!formData.province_id) missing.push('provincia');
      if (!formData.city_id) missing.push('città');
      if (!formData.arrival_date) missing.push('data di arrivo');
      if (!formData.title?.trim()) missing.push('titolo');
      if (!formData.permanent && !formData.end_date) missing.push('data di fine');
      if (missing.length) {
        toast.error(`Compila: ${missing.join(', ')}`);
        return;
      }
      if (!formData.permanent && formData.arrival_date && formData.end_date) {
        if (formData.end_date < formData.arrival_date) {
          toast.error('La data di fine deve essere maggiore o uguale alla data di arrivo');
          return;
        }
      }
    }
    try {
      // Pulisci i dati: rimuovi campi vuoti per announcement
      const cleanData = { ...formData };
      if (formData.news_type === 'announcement') {
        delete cleanData.country_id;
        delete cleanData.province_id;
        delete cleanData.city_id;
        delete cleanData.arrival_date;
        delete cleanData.end_date;
        delete cleanData.permanent;
      }
      
      if (editingNews) {
        await axios.put(`/member-news/${editingNews.id}`, cleanData);
        toast.success('Novità aggiornata con successo');
      } else {
        await axios.post('/member-news', cleanData);
        toast.success('Novità pubblicata con successo');
      }
      setDialogOpen(false);
      setEditingNews(null);
      setFormData({
        news_type: 'announcement',
        title: '',
        message: '',
        link: '',
        country_id: null,
        province_id: null,
        city_id: null,
        arrival_date: '',
        end_date: '',
        permanent: false
      });
      await fetchNews();
    } catch (error) {
      console.error('Failed to create news', error);
      toast.error('Errore nella pubblicazione');
    }
  };

  if (permissionsLoading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex-1 flex items-center justify-center p-6">Caricamento...</div>
      </div>
    );
  }

  if (!canViewBoard) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center p-6">
          <PermissionDeniedCard message="Non hai i permessi per visualizzare la bacheca." />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <div className="pt-20 p-6 max-w-7xl mx-auto">
        <div className="mb-6 flex justify-between items-center">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <MessageSquare className="h-6 w-6" />
              <h1 className="text-3xl font-bold">Bacheca</h1>
            </div>
          </div>
          <div>
          {hasPermission('BOARD_CREATE') && (
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) {
                setEditingNews(null);
                setFormData({
                  news_type: 'announcement',
                  title: '',
                  message: '',
                  link: '',
                  country_id: null,
                  province_id: null,
                  city_id: null,
                  arrival_date: '',
                  end_date: '',
                  permanent: false
                });
              }
            }}>
              <DialogTrigger asChild>
                <Button className="bg-black text-white hover:bg-gray-800">
                  + Nuova novità
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{editingNews ? 'Modifica novità' : 'Pubblica novità'}</DialogTitle>
                </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Tipo Novità */}
                <div>
                  <Label htmlFor="news_type">Tipo di Novità</Label>
                  <Select
                    value={formData.news_type}
                    onValueChange={(value) => setFormData({...formData, news_type: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="announcement">Annuncio generico</SelectItem>
                      <SelectItem value="city_change">Cambio città</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="title">Titolo</Label>
                  <Input
                    id="title"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                  />
                </div>
                
                <div>
                  <Label htmlFor="message">{formData.news_type === 'announcement' ? 'Descrizione' : 'Messaggio'}</Label>
                  <Textarea
                    id="message"
                    required
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                  />
                </div>

                {/* Campi per Annuncio Generico */}
                {formData.news_type === 'announcement' && (
                  <div>
                    <Label htmlFor="link">Link (opzionale)</Label>
                    <Input
                      id="link"
                      type="url"
                      placeholder="https://..."
                      value={formData.link}
                      onChange={(e) => setFormData({...formData, link: e.target.value})}
                    />
                  </div>
                )}

                {/* Campi per Cambio Città */}
                {formData.news_type === 'city_change' && (
                  <div className="border-t pt-4">
                    <h3 className="font-semibold mb-3">Informazioni cambio città</h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="country">Nazione</Label>
                      <Select
                        value={formData.country_id?.toString() || ''}
                        onValueChange={handleCountryChange}
                        aria-required={formData.news_type === 'city_change'}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleziona nazione" />
                        </SelectTrigger>
                        <SelectContent>
                          {countries.map(country => (
                            <SelectItem key={country.id} value={country.id.toString()}>
                              {country.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="province">Provincia</Label>
                      <Select
                        value={formData.province_id?.toString() || ''}
                        onValueChange={handleProvinceChange}
                        disabled={!formData.country_id}
                        aria-required={formData.news_type === 'city_change'}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleziona provincia" />
                        </SelectTrigger>
                        <SelectContent>
                          {provinces.map(province => (
                            <SelectItem key={province.id} value={province.id.toString()}>
                              {province.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="city">Città</Label>
                      <Select
                        value={formData.city_id?.toString() || ''}
                        onValueChange={(value) => setFormData({...formData, city_id: parseInt(value)})}
                        disabled={!formData.province_id}
                        aria-required={formData.news_type === 'city_change'}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleziona città" />
                        </SelectTrigger>
                        <SelectContent>
                          {cities.map(city => (
                            <SelectItem key={city.id} value={city.id.toString()}>
                              {city.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="arrival_date">Data di Arrivo</Label>
                      <Input
                        id="arrival_date"
                        type="date"
                        value={formData.arrival_date}
                        onChange={(e) => setFormData({...formData, arrival_date: e.target.value})}
                        required={formData.news_type === 'city_change'}
                      />
                    </div>

                    <div>
                      <Label htmlFor="end_date">Data di Fine</Label>
                      <Input
                        id="end_date"
                        type="date"
                        value={formData.end_date}
                        onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                        disabled={formData.permanent}
                        required={formData.news_type === 'city_change' && !formData.permanent}
                      />
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="permanent"
                        checked={formData.permanent}
                        onCheckedChange={(checked) => setFormData({...formData, permanent: checked, end_date: checked ? '' : formData.end_date})}
                      />
                      <Label htmlFor="permanent">Sempre</Label>
                    </div>
                  </div>
                  </div>
                )}

                <Button type="submit" className="bg-black text-white hover:bg-gray-800 w-full">
                  {editingNews ? 'Aggiorna' : 'Pubblica'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          )}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">Caricamento...</div>
        ) : news.length === 0 ? (
          <div className="text-center py-12 text-gray-600">
            Nessuna novità disponibile.
          </div>
        ) : (
          <>
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-gray-700">
                <strong>Benvenuto nella Bacheca!</strong> Questo spazio è dedicato alle comunicazioni generali e agli annunci utili alla community: opportunità, eventi, iniziative, richieste di collaborazione e informazioni di interesse comune.
                <br /><br />
                Puoi inoltre segnalare i tuoi spostamenti o cambi di città per facilitare incontri e connessioni tra i membri. In questi casi, indica chiaramente la destinazione, il periodo di permanenza e un canale di contatto: i membri della città di destinazione riceveranno una notifica.
              </p>
            </div>
            
            <div className="grid grid-cols-1 gap-6">
            {news.map((item) => (
              <Card key={item.id} className="border border-gray-200">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle>{item.title}</CardTitle>
                      <p className="text-sm text-gray-500">
                        Pubblicato da {item.author_name} il {new Date(item.created_at).toLocaleDateString('it-IT')}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {/* Mostra Modifica se: ha BOARD_EDIT_ALL o (ha BOARD_EDIT_OWN e è l'autore) */}
                      {(hasPermission('BOARD_EDIT_ALL') || (hasPermission('BOARD_EDIT_OWN') && item.is_own)) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(item)}
                          className="border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white"
                        >
                          Modifica
                        </Button>
                      )}
                      
                      {/* Mostra Elimina se: ha BOARD_DELETE_ALL o (ha BOARD_DELETE_OWN e è l'autore) */}
                      {(hasPermission('BOARD_DELETE_ALL') || (hasPermission('BOARD_DELETE_OWN') && item.is_own)) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(item.id)}
                          className="border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
                        >
                          Elimina
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="mb-2">
                    <span className={`inline-block px-2 py-1 text-xs rounded ${item.news_type === 'announcement' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                      {item.news_type === 'announcement' ? '📢 Annuncio' : '📍 Cambio città'}
                    </span>
                  </div>
                  
                  <p
                    className="mb-4 text-gray-700 leading-relaxed whitespace-pre-line break-words"
                    style={messagePreviewStyle}
                  >
                    {item.message}
                  </p>
                  
                  <div className="mb-4 flex flex-wrap items-center gap-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedNews(item)}
                      className="border-gray-300 text-gray-700 hover:bg-gray-100"
                    >
                      Leggi tutto
                    </Button>

                    {/* Link per annunci generici */}
                    {item.news_type === 'announcement' && item.link && (
                      <a 
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                      >
                        🔗 Vai al link
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    )}
                  </div>
                  
                  {/* Info cambio città */}
                  {item.news_type === 'city_change' && item.city_name && (
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-semibold mb-2">📍 Informazioni Destinazione</h4>
                      <p className="text-sm">
                        <strong>Città:</strong> {item.city_name}, {item.province_name} ({item.country_name})
                      </p>
                      {item.arrival_date && (
                        <p className="text-sm">
                          <strong>Dal:</strong> {new Date(item.arrival_date).toLocaleDateString('it-IT')}
                        </p>
                      )}
                      {item.permanent ? (
                        <p className="text-sm">
                          <strong>Permanenza:</strong> Sempre
                        </p>
                      ) : item.end_date && (
                        <p className="text-sm">
                          <strong>Al:</strong> {new Date(item.end_date).toLocaleDateString('it-IT')}
                        </p>
                      )}
                      {item.author_phone && (
                        <p className="text-sm mt-2">
                          <strong>📱 Contatto:</strong> {item.author_phone}
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          <Dialog open={Boolean(selectedNews)} onOpenChange={(open) => !open && setSelectedNews(null)}>
            {selectedNews && (
              <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
                <DialogHeader className="pr-8">
                  <div className="mb-2">
                    <span className={`inline-block px-2 py-1 text-xs rounded ${selectedNews.news_type === 'announcement' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                      {selectedNews.news_type === 'announcement' ? '📢 Annuncio' : '📍 Cambio città'}
                    </span>
                  </div>
                  <DialogTitle className="text-2xl leading-tight">{selectedNews.title}</DialogTitle>
                  <p className="text-sm text-gray-500">
                    Pubblicato da {selectedNews.author_name} il {new Date(selectedNews.created_at).toLocaleDateString('it-IT')}
                  </p>
                </DialogHeader>

                <div className="space-y-4">
                  <div className="rounded-lg border bg-gray-50 p-4">
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap break-words">
                      {selectedNews.message}
                    </p>
                  </div>

                  {selectedNews.news_type === 'announcement' && selectedNews.link && (
                    <div>
                      <a
                        href={selectedNews.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                      >
                        🔗 Vai al link
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    </div>
                  )}

                  {selectedNews.news_type === 'city_change' && selectedNews.city_name && (
                    <div className="rounded-lg bg-blue-50 p-4">
                      <h4 className="font-semibold mb-2">📍 Informazioni Destinazione</h4>
                      <p className="text-sm">
                        <strong>Città:</strong> {selectedNews.city_name}, {selectedNews.province_name} ({selectedNews.country_name})
                      </p>
                      {selectedNews.arrival_date && (
                        <p className="text-sm">
                          <strong>Dal:</strong> {new Date(selectedNews.arrival_date).toLocaleDateString('it-IT')}
                        </p>
                      )}
                      {selectedNews.permanent ? (
                        <p className="text-sm">
                          <strong>Permanenza:</strong> Sempre
                        </p>
                      ) : selectedNews.end_date && (
                        <p className="text-sm">
                          <strong>Al:</strong> {new Date(selectedNews.end_date).toLocaleDateString('it-IT')}
                        </p>
                      )}
                      {selectedNews.author_phone && (
                        <p className="text-sm mt-2">
                          <strong>📱 Contatto:</strong> {selectedNews.author_phone}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </DialogContent>
            )}
          </Dialog>
          </>
        )}
      </div>
    </div>
  );
}
